import { NgModule } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { AutocompleteValidationDirective } from './directives/autocomplete-validation.directive';
import { MatAutocompleteModule as MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule as MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule as MatInputModule } from '@angular/material/input';
import { MatMenuModule as MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule as MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule as MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule as MatRadioModule } from '@angular/material/radio';
import { MatSelectModule as MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule as MatTableModule } from '@angular/material/table';
import { MatTabsModule as MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule as MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AlphaNumericDirective } from './directives/alpha-numeric.directive';
import { DisableControlDirective } from './directives/disable-control.directive';
import { DebounceClickDirective } from './directives/debounce-click.directive';
import { SharedRoutingModule } from './shared-routing.module';
import { FileInputComponent } from './components/file-input/file-input.component';
import { MomentAfterValidator } from './validators/moment.after.validator';
import { MomentBeforeValidator } from './validators/moment.before.validator';
import { WeightValidationDirective } from './directives/weight-validation.directive';
import { DecimalNumberComponent } from './control-value-accessors/decimal-number/decimal-number.component';
import { ModuleService } from '../pages/secure/admin/shared/module.service';
import { UserService } from '../pages/secure/admin/manage-users/shared/user.service';
import { OrderHeaderComponent } from '../pages/secure/order/order-header/order-header.component';
import { OrderService } from '../pages/secure/order/shared/order.service';
import { OrderDocumentService } from '../pages/secure/order/shared/order-document.service';
import { CmrValidationService } from '../pages/secure/order/shared/cmr-validation.service';
import { OrderAcceptDeclineService } from '../pages/secure/order/shared/order-accept-decline.service';
import { OrderNotificationService } from '../pages/secure/order/shared/order-notification.service';
import { TimeslotBookingComponent } from '../pages/secure/order/timeslot-booking/timeslot-booking.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ContainerService } from '../pages/secure/order/shared/container.service';
import { ContainerCompartmentComponent } from '../pages/secure/order/transport-information/container-compartment/container-compartment.component';
import { DragDropFileInputComponent } from './components/drag-drop-file-input/drag-drop-file-input.component';
import { DragDropDirective } from './directives/drag-and-drop.directive';
import { ToUpperCaseDirective } from './directives/to-upper-case.directive';
import { FileService } from './services/file.service';
import { ProgressBarButtonComponent } from './components/progress-bar-button/progress-bar-button.component';
import { ModalService } from '../core/services/modal.service';
import { ToLowerCaseDirective } from './directives/to-lower-case.directive';
import { LoadingIndicatorComponent } from './components/loading-indicator/loading-indicator.component';
import { SafePipe } from './pipes/safe.pipe';
import { TransportDetailsComponent } from '../pages/secure/order/transport-information/transport-details/transport-details.component';
import { FormatDirective } from './directives/format.directive';
import { TankContainerNrDirective } from './directives/tank-container-number.directive';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IncidentReportingService } from '../pages/secure/order/shared/incident-reporting.service';
import { UploadDocumentComponent } from './components/upload-document/upload-document.component';
import { NoAvailableDataComponent } from './components/no-available-data/no-available-data.component';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { InforitLibraryModule } from './components/filter-menu/inforit-filter-menu.module';
import { MatButtonModule as MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule as MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule as MatDialogModule } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCheckboxModule as MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule as MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatBadgeModule } from '@angular/material/badge';
import { CdkDetailRowDirective } from './directives/cdk-detail-row.directive';
import { AddCarrierCertificationComponent } from './components/add-carrier-certification/add-carrier-certification.component';
import { CertificatesHubService } from './services/certificates-hub.service';
import { MatListModule as MatListModule } from '@angular/material/list';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { CarrierProfileComponent } from '../pages/secure/admin/carriers/carrier-profile/carrier-profile.component';
import { FreightExchangeService } from '../pages/secure/freight-exchange/shared/freight-exchange.service';
import { OrderMoreInformationComponent } from '../pages/secure/order/order-more-information/order-more-information.component';
import { CarrierProfileGuard } from '../pages/secure/admin/carriers/guards/carrier-profile.guard';
import { CarrierProfileDataGuard } from '../pages/secure/admin/carriers/guards/carrier-profile-data.guard';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

const modules = [
  MatPaginatorModule,
  MatProgressSpinnerModule,
  MatSortModule,
  MatSelectModule,
  MatTableModule,
  MatAutocompleteModule,
  FormsModule,
  MatInputModule,
  ReactiveFormsModule,
  MatTabsModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatMenuModule,
  MatRadioModule,
  MatTooltipModule,
  MatExpansionModule,
  MatCardModule,
  MatProgressBarModule,
  InforitLibraryModule,
  MatMenuModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatFormFieldModule,
  MatInputModule,
  MatIconModule,
  MatCardModule,
  MatDialogModule,
  MatSelectModule,
  MatToolbarModule,
  MatSidenavModule,
  MatSelectModule,
  MatRadioModule,
  MatSlideToggleModule,
  MatCheckboxModule,
  MatDatepickerModule,
  NgxMatTimepickerModule,
  MatNativeDateModule,
  MatBadgeModule,
  MatListModule,
];

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  imports: [
    CommonModule,
    modules,
    NgxSpinnerModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SharedRoutingModule,
    NgxMaskDirective,
    NgxMaskPipe,
    DragDropModule,
    NgbModule,
  ],
  declarations: [
    AutocompleteValidationDirective,
    AlphaNumericDirective,
    DisableControlDirective,
    DebounceClickDirective,
    FileInputComponent,
    UploadDocumentComponent,
    NoAvailableDataComponent,
    SearchInputComponent,
    MomentAfterValidator,
    MomentBeforeValidator,
    WeightValidationDirective,
    DecimalNumberComponent,
    OrderHeaderComponent,
    TimeslotBookingComponent,
    ContainerCompartmentComponent,
    ToUpperCaseDirective,
    FormatDirective,
    ToLowerCaseDirective,
    TankContainerNrDirective,
    DragDropFileInputComponent,
    AddCarrierCertificationComponent,
    DragDropDirective,
    ProgressBarButtonComponent,
    LoadingIndicatorComponent,
    SafePipe,
    TransportDetailsComponent,
    CdkDetailRowDirective,
    BreadcrumbComponent,
    CarrierProfileComponent,
    OrderMoreInformationComponent,
  ],
  exports: [
    modules,
    NgxMaskDirective,
    NgxMaskPipe,
    AutocompleteValidationDirective,
    TranslateModule,
    AlphaNumericDirective,
    DisableControlDirective,
    DebounceClickDirective,
    WeightValidationDirective,
    OrderHeaderComponent,
    FileInputComponent,
    UploadDocumentComponent,
    NoAvailableDataComponent,
    SearchInputComponent,
    MomentAfterValidator,
    MomentBeforeValidator,
    DecimalNumberComponent,
    NgbModule,
    ToUpperCaseDirective,
    FormatDirective,
    ToLowerCaseDirective,
    TankContainerNrDirective,
    DragDropDirective,
    DragDropFileInputComponent,
    AddCarrierCertificationComponent,
    ProgressBarButtonComponent,
    LoadingIndicatorComponent,
    SafePipe,
    CdkDetailRowDirective,
    BreadcrumbComponent,
    CarrierProfileComponent,
    DragDropModule,
    NgxSpinnerModule,
    OrderMoreInformationComponent,
  ],
  providers: [
    provideNgxMask({
      thousandSeparator: '.',
      decimalMarker: ',',
      dropSpecialCharacters: false,
    }),
    ModuleService,
    UserService,
    OrderDocumentService,
    OrderService,
    CmrValidationService,
    OrderAcceptDeclineService,
    OrderNotificationService,
    ContainerService,
    FileService,
    ModalService,
    IncidentReportingService,
    FreightExchangeService,
    CarrierProfileGuard,
    CarrierProfileDataGuard,
  ],
})
export class SharedModule {}
